import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Box, 
    Typography, 
    IconButton, 
    Menu, 
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Divider,
    Avatar,
    Card,
    CardHeader,
    CardContent,
    CardActions
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { deleteTale, updateTale } from '../../../actions/tales';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CommentIcon from '@mui/icons-material/Comment';
import { generateTaleUrl } from '../../../utils/urlUtils';
import { useAuth } from '../../../contexts/AuthContext';
import LoginModal from '../../Auth/LoginModal';
import axios from 'axios';

const Tale = ({ tale, currentUser }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    
    const getCurrentUserId = () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return null;
            
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            return JSON.parse(jsonPayload).id;
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    };

    const currentUserId = getCurrentUserId();

    const handleTaleClick = () => {
        navigate(`/tales/${tale._id}`);
    };

    const handleEdit = (e) => {
        e.stopPropagation();
        navigate('/form', { 
            state: { 
                isEditing: true,
                taleData: {
                    id: tale._id,
                    title: tale.title,
                    story: tale.story,
                    group: tale.group,
                    tags: tale.tags || [],
                    groupColor: tale.groupColor
                }
            } 
        });
    };

    const handleDelete = async (e) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this tale?')) {
            try {
                await dispatch(deleteTale(tale._id));
            } catch (error) {
                console.error('Error deleting tale:', error);
            }
        }
    };

    const isOwner = tale.creator?._id === currentUserId;


    const [anchorEl, setAnchorEl] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editedTale, setEditedTale] = useState({
        title: '',
        story: '',
        group: '',
        groupColor: ''
    });
    const open = Boolean(anchorEl);

    const isCreator = currentUser?._id === tale.creator?._id;

    const handleViewComments = (e) => {
        e.stopPropagation();
        navigate(`/tales/${tale._id}`);
    };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleEditClick = () => {
        handleClose();
        navigate('/create', { 
            state: { 
                isEditing: true,
                taleData: {
                    id: tale._id,
                    title: tale.title,
                    story: tale.story,
                    group: tale.group,
                    groupColor: tale.groupColor
                }
            }
        });
    };

    const handleDeleteClick = () => {
        handleClose();
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        dispatch(deleteTale(tale._id));
        setDeleteDialogOpen(false);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
    };

    const handleEditCancel = () => {
        setEditDialogOpen(false);
    };

    const handleEditConfirm = () => {
        dispatch(updateTale(tale._id, editedTale));
        setEditDialogOpen(false);
    };

    // Generate the encoded URL when the tale is rendered
    const taleUrl = generateTaleUrl(tale);

    const [showLoginModal, setShowLoginModal] = useState(false);
    const { isAuthenticated, user: authUser } = useAuth();

    return (
        <Link 
            to={taleUrl}
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={(e) => {
                // Update the browser URL without triggering a page reload
                window.history.pushState({}, '', taleUrl);
            }}
        >
            <Card 
                onClick={handleTaleClick}
                sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                        boxShadow: 6
                    }
                }}
            >
                <Box sx={{ 
                    bgcolor: tale.groupColor || 'primary.main',
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box display="flex" alignItems="center">
                        <Avatar 
                            src={tale.creator?.imageUrl} 
                            alt={tale.creator?.username || 'Anonymous'}
                            sx={{ marginRight: 1 }}
                        >
                            {tale.creator?.username?.[0]?.toUpperCase() || 'A'}
                        </Avatar>
                        <Box>
                            <Typography variant="h6" component="div" color="white">
                                {tale.title}
                            </Typography>
                            <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                                Posted by {tale.creator?.username || 'Anonymous'}
                            </Typography>
                        </Box>
                    </Box>
                    {isOwner && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton 
                                size="small" 
                                onClick={handleEdit}
                                sx={{ color: 'white' }}
                            >
                                <EditIcon />
                            </IconButton>
                            <IconButton 
                                size="small" 
                                onClick={handleDelete}
                                sx={{ color: 'white' }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    )}
                </Box>
                <CardContent>
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {tale.story}
                    </Typography>
                </CardContent>
                <CardActions disableSpacing sx={{ 
                    justifyContent: 'flex-end',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    pr: 2
                }}>
                    <Box display="flex" alignItems="center">
                        <CommentIcon sx={{ fontSize: 'small', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                            {tale.reactionCount || 0}
                        </Typography>
                    </Box>
                </CardActions>

                <Menu
                    id="tale-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        elevation: 2,
                        sx: {
                            minWidth: '120px',
                            borderRadius: '8px',
                            mt: 1
                        }
                    }}
                >
                    {isCreator && (
                        <>
                            <MenuItem 
                                onClick={handleEditClick}
                                sx={{
                                    fontSize: '14px',
                                    py: 1,
                                    '&:hover': {
                                        color: tale.groupColor || '#3498db'
                                    }
                                }}
                            >
                                Edit
                            </MenuItem>
                            <MenuItem 
                                onClick={handleDeleteClick}
                                sx={{
                                    fontSize: '14px',
                                    py: 1,
                                    color: '#ef4444',
                                    '&:hover': {
                                        color: '#dc2626'
                                    }
                                }}
                            >
                                Delete
                            </MenuItem>
                        </>
                    )}
                </Menu>

                {/* Edit Dialog */}
                <Dialog
                    open={editDialogOpen}
                    onClose={handleEditCancel}
                    aria-labelledby="edit-dialog-title"
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '12px'
                        }
                    }}
                >
                    <DialogTitle 
                        id="edit-dialog-title"
                        sx={{
                            pb: 1,
                            '& .MuiTypography-root': {
                                fontSize: '1.25rem',
                                fontWeight: 600
                            }
                        }}
                    >
                        Edit Tale
                    </DialogTitle>
                    <DialogContent sx={{ pt: '16px !important' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Title"
                            type="text"
                            fullWidth
                            value={editedTale.title}
                            onChange={(e) => setEditedTale({
                                ...editedTale,
                                title: e.target.value
                            })}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px'
                                }
                            }}
                        />
                        <TextField
                            margin="dense"
                            label="Story"
                            type="text"
                            fullWidth
                            multiline
                            rows={4}
                            value={editedTale.story}
                            onChange={(e) => setEditedTale({
                                ...editedTale,
                                story: e.target.value
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px'
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, gap: 1 }}>
                        <Button 
                            onClick={handleEditCancel}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleEditConfirm} 
                            variant="contained"
                            sx={{
                                bgcolor: tale.groupColor || '#3498db',
                                '&:hover': {
                                    bgcolor: tale.groupColor ? `${tale.groupColor}dd` : '#2980b9'
                                }
                            }}
                        >
                            Save Changes
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleDeleteCancel}
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            maxWidth: '400px'
                        }
                    }}
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        <Typography variant="h6" fontWeight={600}>
                            Delete Tale
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" color="text.secondary">
                            Are you sure you want to delete this tale? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, gap: 1 }}>
                        <Button 
                            onClick={handleDeleteCancel}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleDeleteConfirm} 
                            variant="contained"
                            sx={{
                                bgcolor: '#ef4444',
                                '&:hover': {
                                    bgcolor: '#dc2626'
                                }
                            }}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
                <LoginModal open={showLoginModal} onClose={() => setShowLoginModal(false)} />
            </Card>
        </Link>
    );
};

export default Tale;